import React, { useState, useEffect, useRef } from 'react';
import { ivrAudioAssetsAPI } from '../services/api';

const TTS_PROVIDERS = [
  { id: 'elevenlabs', name: 'ElevenLabs', description: 'High-quality, natural voices' },
  { id: 'openai', name: 'OpenAI (GPT-4o)', description: 'Fast, reliable voices' },
  { id: 'cartesia', name: 'Cartesia', description: 'Ultra-low latency voices' }
];

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  generating: 'bg-yellow-100 text-yellow-800',
  ready: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-800'
};

export default function IVRAudioAssetsPage() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    text: '',
    language: 'en-US',
    ttsProvider: 'elevenlabs',
    voiceId: '',
    voiceName: ''
  });

  // Voice selection
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);

  // Preview state
  const [previewing, setPreviewing] = useState(false);
  const [previewAudio, setPreviewAudio] = useState(null);
  const audioRef = useRef(null);

  // Saving state
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(null);

  // Load assets
  useEffect(() => {
    loadAssets();
  }, []);

  // Load voices when provider changes
  useEffect(() => {
    if (formData.ttsProvider) {
      loadVoices(formData.ttsProvider);
    }
  }, [formData.ttsProvider]);

  const loadAssets = async () => {
    try {
      setLoading(true);
      const response = await ivrAudioAssetsAPI.getAssets();
      setAssets(response.data.assets || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadVoices = async (provider) => {
    try {
      setLoadingVoices(true);
      const response = await ivrAudioAssetsAPI.getVoices(provider);
      setVoices(response.data.voices || []);
    } catch (err) {
      console.error('Failed to load voices:', err);
      setVoices([]);
    } finally {
      setLoadingVoices(false);
    }
  };

  const handleOpenForm = (asset = null) => {
    if (asset) {
      setEditingAsset(asset);
      setFormData({
        name: asset.name,
        description: asset.description || '',
        text: asset.text,
        language: asset.language,
        ttsProvider: asset.ttsProvider,
        voiceId: asset.voiceId,
        voiceName: asset.voiceName || ''
      });
    } else {
      setEditingAsset(null);
      setFormData({
        name: '',
        description: '',
        text: '',
        language: 'en-US',
        ttsProvider: 'elevenlabs',
        voiceId: '',
        voiceName: ''
      });
    }
    setShowForm(true);
    setPreviewAudio(null);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingAsset(null);
    setPreviewAudio(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Update voice name when voice changes
    if (name === 'voiceId') {
      const voice = voices.find(v => v.id === value);
      if (voice) {
        setFormData(prev => ({ ...prev, voiceName: voice.name }));
      }
    }
  };

  const handlePreview = async () => {
    if (!formData.text || !formData.voiceId) {
      alert('Please enter text and select a voice');
      return;
    }

    try {
      setPreviewing(true);
      const response = await ivrAudioAssetsAPI.previewAudio({
        text: formData.text.substring(0, 500), // Limit preview to 500 chars
        ttsProvider: formData.ttsProvider,
        voiceId: formData.voiceId,
        language: formData.language
      });

      if (response.data.audioBase64) {
        const audioSrc = `data:${response.data.mimeType};base64,${response.data.audioBase64}`;
        setPreviewAudio(audioSrc);

        // Auto-play
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.play();
          }
        }, 100);
      }
    } catch (err) {
      alert(`Preview failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.text || !formData.voiceId) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);

      if (editingAsset) {
        await ivrAudioAssetsAPI.updateAsset(editingAsset.id, formData);
      } else {
        await ivrAudioAssetsAPI.createAsset(formData);
      }

      await loadAssets();
      handleCloseForm();
    } catch (err) {
      alert(`Save failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async (assetId) => {
    try {
      setGenerating(assetId);
      await ivrAudioAssetsAPI.generateAudio(assetId);
      await loadAssets();
    } catch (err) {
      alert(`Generation failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setGenerating(null);
    }
  };

  const handleDelete = async (assetId) => {
    if (!confirm('Are you sure you want to delete this audio asset?')) {
      return;
    }

    try {
      await ivrAudioAssetsAPI.deleteAsset(assetId);
      await loadAssets();
    } catch (err) {
      alert(`Delete failed: ${err.response?.data?.error || err.message}`);
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    const seconds = Math.round(ms / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">IVR Audio Assets</h1>
            <p className="text-gray-600 mt-1">
              Create and manage pre-generated audio for Emergency IVR flows
            </p>
          </div>
          <button
            onClick={() => handleOpenForm()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Audio Asset
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Assets List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading audio assets...</p>
          </div>
        ) : assets.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No audio assets yet</h3>
            <p className="mt-1 text-gray-500">Create your first audio asset for the Emergency IVR.</p>
            <button
              onClick={() => handleOpenForm()}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Create Audio Asset
            </button>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider / Voice</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {assets.map(asset => (
                  <tr key={asset.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{asset.name}</div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">{asset.text}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 capitalize">{asset.ttsProvider}</div>
                      <div className="text-sm text-gray-500">{asset.voiceName || asset.voiceId}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[asset.status]}`}>
                        {asset.status}
                      </span>
                      {asset.errorMessage && (
                        <div className="text-xs text-red-500 mt-1">{asset.errorMessage}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div>{formatDuration(asset.durationMs)}</div>
                      <div className="text-xs">{formatFileSize(asset.fileSizeBytes)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {asset.status === 'ready' && asset.audioUrl && (
                          <audio
                            controls
                            src={asset.audioUrl}
                            className="h-8"
                            style={{ width: '120px' }}
                          />
                        )}
                        {(asset.status === 'draft' || asset.status === 'error') && (
                          <button
                            onClick={() => handleGenerate(asset.id)}
                            disabled={generating === asset.id}
                            className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            {generating === asset.id ? 'Generating...' : 'Generate'}
                          </button>
                        )}
                        <button
                          onClick={() => handleOpenForm(asset)}
                          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(asset.id)}
                          className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">
                    {editingAsset ? 'Edit Audio Asset' : 'Create Audio Asset'}
                  </h2>
                  <button onClick={handleCloseForm} className="text-gray-500 hover:text-gray-700">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      placeholder="e.g., Main Menu Greeting"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      placeholder="Optional description"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  {/* Text */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Text to Speak <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="text"
                      value={formData.text}
                      onChange={handleFormChange}
                      rows={4}
                      placeholder="Enter the text that will be converted to speech..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.text.length} characters
                    </p>
                  </div>

                  {/* TTS Provider */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      TTS Provider <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {TTS_PROVIDERS.map(provider => (
                        <label
                          key={provider.id}
                          className={`flex flex-col p-3 border rounded-lg cursor-pointer transition-colors ${
                            formData.ttsProvider === provider.id
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="ttsProvider"
                            value={provider.id}
                            checked={formData.ttsProvider === provider.id}
                            onChange={handleFormChange}
                            className="sr-only"
                          />
                          <span className="font-medium text-sm">{provider.name}</span>
                          <span className="text-xs text-gray-500">{provider.description}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Voice Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Voice <span className="text-red-500">*</span>
                    </label>
                    {loadingVoices ? (
                      <div className="text-sm text-gray-500">Loading voices...</div>
                    ) : (
                      <select
                        name="voiceId"
                        value={formData.voiceId}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select a voice...</option>
                        {voices.map(voice => (
                          <option key={voice.id} value={voice.id}>
                            {voice.name} {voice.category && `(${voice.category})`}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Language */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Language
                    </label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="en-US">English (US)</option>
                      <option value="en-GB">English (UK)</option>
                      <option value="es-ES">Spanish</option>
                      <option value="es-MX">Spanish (Mexico)</option>
                      <option value="fr-FR">French</option>
                      <option value="de-DE">German</option>
                      <option value="it-IT">Italian</option>
                      <option value="pt-BR">Portuguese (Brazil)</option>
                    </select>
                  </div>

                  {/* Preview Section */}
                  <div className="border-t pt-4">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={handlePreview}
                        disabled={previewing || !formData.text || !formData.voiceId}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        {previewing ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Generating Preview...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Preview Audio
                          </>
                        )}
                      </button>

                      {previewAudio && (
                        <audio
                          ref={audioRef}
                          controls
                          src={previewAudio}
                          className="flex-1"
                        />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Preview is limited to the first 500 characters
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                  <button
                    onClick={handleCloseForm}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : (editingAsset ? 'Update Asset' : 'Create Asset')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
